// Server-side user preferences: GET/PATCH /api/v1/me/preferences. The web keeps localStorage as a fast
// first-paint cache and syncs it with these on sign-in and on change.
import { z } from "zod";

export const THEMES = ["system", "light", "dark"] as const;
export const DENSITIES = ["compact", "comfortable"] as const;
export type ThemePreference = (typeof THEMES)[number];
export type DensityPreference = (typeof DENSITIES)[number];

/** Serialized size (UTF-8 bytes of the JSON) the free-form `ui` object may reach. */
export const UI_PREFS_MAX_BYTES = 8 * 1024;
const UI_KEY = /^[A-Za-z0-9_.:-]{1,64}$/;

/** JSON value a client may park in `ui`. */
export type UiPrefValue = string | number | boolean | null | UiPrefValue[] | { [key: string]: UiPrefValue };

export function jsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

const UiObject = z
  .record(z.string(), z.json())
  .refine((o) => Object.keys(o).every((k) => UI_KEY.test(k)), { message: "ui keys: 1-64 of A-Z a-z 0-9 _ . : -" })
  .refine((o) => jsonBytes(o) <= UI_PREFS_MAX_BYTES, { message: `ui is limited to ${UI_PREFS_MAX_BYTES} bytes` });

/** The known keys; each is optional (unset = the client default). */
export const UserPreferencesShape = {
  theme: z.enum(THEMES),
  density: z.enum(DENSITIES),
  sidebarCollapsed: z.boolean(),
  /** WCAG 2.1.4: single-key shortcuts can be switched off. */
  singleKeyShortcuts: z.boolean(),
  /**
   * Deprecated and ignored: translations of content are always shown when they exist (there is a per-item
   * "Show original" toggle instead). The key stays valid so older clients and stored values keep working.
   */
  autoShowTranslations: z.boolean(),
  /** Free-form client state (view layouts, "show archived", ...). */
  ui: UiObject,
};

/** What GET returns: only the keys that were ever saved. */
export const UserPreferences = z.strictObject(UserPreferencesShape).partial();
export type UserPreferences = z.infer<typeof UserPreferences>;

/**
 * PATCH body. Known keys replace; within `ui`, keys merge into the stored object and a `null` value removes the key.
 * Unknown top-level keys are rejected (a typo must not be silently stored).
 */
export const UserPreferencesPatch = z.strictObject(UserPreferencesShape).partial();
export type UserPreferencesPatch = z.infer<typeof UserPreferencesPatch>;

/** Applies a patch to stored preferences (see UserPreferencesPatch); the caller checks the merged `ui` size. */
export function mergePreferences(stored: UserPreferences, patch: UserPreferencesPatch): UserPreferences {
  const { ui, ...known } = patch;
  const next: UserPreferences = { ...stored, ...known };
  if (ui !== undefined) {
    const merged: Record<string, UiPrefValue> = { ...(stored.ui as Record<string, UiPrefValue> | undefined) };
    for (const [k, v] of Object.entries(ui)) {
      if (v === null) delete merged[k];
      else merged[k] = v as UiPrefValue;
    }
    if (Object.keys(merged).length > 0) next.ui = merged;
    else delete next.ui;
  }
  return next;
}
